import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import { SITES } from "@/lib/sites";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", site: "District" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/tickets");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="register-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-md bg-zinc-950 text-white grid place-items-center">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div className="font-heading font-bold text-lg">HelpDesk · ESUSD</div>
        </div>
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Register</div>
        <h2 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Create your account</h2>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Full name</Label>
            <Input required data-testid="register-name-input" value={form.name} onChange={onChange("name")}
              className="h-11 rounded-md" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Email</Label>
            <Input required type="email" data-testid="register-email-input" value={form.email} onChange={onChange("email")}
              className="h-11 rounded-md" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Your site / school</Label>
            <Select value={form.site} onValueChange={(v) => setForm({ ...form, site: v })}>
              <SelectTrigger className="h-11 rounded-md" data-testid="register-site-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SITES.map((s) => (<SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Password</Label>
            <Input required type="password" minLength={6} data-testid="register-password-input" value={form.password} onChange={onChange("password")}
              className="h-11 rounded-md" />
          </div>
          <Button type="submit" disabled={busy} data-testid="register-submit-button"
            className="w-full h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] transition-colors duration-200 text-white font-medium">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-sm text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0055FF] hover:text-[#0040CC] font-medium">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
